// [PTG_ONE_LEAGUE_LIST 2026-10-04] THE LEAGUE PICKER'S OPTIONS, FROM THE ONE LIST.
//
// This replaces SPORTS_OPTIONS in constants/Constant.js, a hand-kept copy of
// the producer's league list that had to be edited by hand every time a league
// was added. The one source is utils/leagues.json in flask-app; the backend
// serves it at GET /api/leagues as
//   { count, leagues: [{ league, sport, group, label }] }
//
// It is rebuilt into the exact shape the picker always used:
//   [{ value: "All", label: "All" },
//    { value: group, label: group, children: [{ value: league, label }] }, ...]
// groups and leagues in the list's own order (the daily post order).
//
// Fetched once per page load and shared by every picker on the page.
import { useEffect, useState } from "react";
import { BACKEND_URL } from "./Constant";

let cache = null;
let inflight = null;

function buildOptions(rows) {
  if (!Array.isArray(rows) || !rows.length) throw new Error("no leagues");
  const groups = [];
  const byGroup = {};
  for (const r of rows) {
    if (!r || !r.league || !r.group) continue;
    if (!byGroup[r.group]) {
      byGroup[r.group] = { value: r.group, label: r.group, children: [] };
      groups.push(byGroup[r.group]);
    }
    byGroup[r.group].children.push({ value: r.league, label: r.label || r.league });
  }
  if (!groups.length) throw new Error("no leagues");
  return [{ value: "All", label: "All" }, ...groups];
}

export function loadSportsOptions() {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetch(BACKEND_URL + "/leagues")
      .then((r) => {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then((doc) => {
        cache = buildOptions(doc && doc.leagues);
        return cache;
      })
      .catch((e) => {
        inflight = null; // let the next mount try again
        throw e;
      });
  }
  return inflight;
}

// options: the picker list (just "All" until loaded)
// ready:   true once the real list is in; the picker stays disabled until then,
//          so nothing can be selected or cleared against an empty list
// error:   set if the list could not be loaded
export function useSportsOptions() {
  const [options, setOptions] = useState(cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (cache) return undefined;
    let live = true;
    loadSportsOptions()
      .then((o) => { if (live) setOptions(o); })
      .catch((e) => {
        console.error("[leagues] could not load the league list:", e.message);
        if (live) setError(e);
      });
    return () => { live = false; };
  }, []);

  return {
    options: options || [{ value: "All", label: "All" }],
    ready: !!options,
    error,
  };
}
